(() => {
  'use strict';

  const cache = new Map();
  let requestSerial = 0;

  const text = (value) => String(value || '').trim();
  const slug = (value) => text(value).toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const mediaUrl = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return value.src || value.url || value.preview_image?.src || '';
  };

  function colorSections() {
    return [...document.querySelectorAll('[data-me-designer-colors]')];
  }

  function productForms() {
    return [...document.querySelectorAll('.me-main-product__form, .me-pp__form')];
  }

  function normalizeProductUrl(value) {
    const raw = text(value);
    if (!raw) return '';
    try {
      const url = new URL(raw, window.location.origin);
      return url.pathname.replace(/\.js$/i, '').replace(/\/$/, '');
    } catch (_) {
      return raw.replace(/\.js$/i, '').replace(/\/$/, '');
    }
  }

  function productJsonUrl(value) {
    const path = normalizeProductUrl(value);
    if (!path) return '';
    if (/^\/products\//i.test(path)) return `${path}.js`;
    return `/products/${encodeURIComponent(path.replace(/^.*\/products\//i, ''))}.js`;
  }

  async function fetchJson(url) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) {
      const error = new Error(`The selected color could not be loaded (${response.status}).`);
      error.status = response.status;
      throw error;
    }
    return response.json();
  }

  async function searchExactProduct(colorName, productNumber, baseTitle) {
    const query = [productNumber, baseTitle, colorName].filter(Boolean).join(' ');
    const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=show`;
    const data = await fetchJson(url);
    const products = data?.resources?.results?.products || [];
    const targetColor = slug(colorName);
    const targetNumber = text(productNumber).toLowerCase();
    const targetBase = slug(baseTitle);

    const exact = products.find((item) => {
      const title = text(item.title);
      const handle = text(item.handle);
      const titleSlug = slug(title);
      const numberMatches = !targetNumber || title.toLowerCase().startsWith(targetNumber) || handle.startsWith(targetNumber);
      const colorMatches = !targetColor || titleSlug.endsWith(targetColor) || slug(handle).endsWith(targetColor);
      const baseMatches = !targetBase || titleSlug.includes(targetBase);
      return numberMatches && colorMatches && baseMatches;
    }) || products.find((item) => slug(item.title).endsWith(targetColor));

    if (!exact?.url && !exact?.handle) return null;
    return normalizeProductUrl(exact.url || `/products/${exact.handle}`);
  }

  async function fetchProduct(reference, context = {}) {
    const initialReference = normalizeProductUrl(reference);
    if (!initialReference) throw new Error('This color is missing its product reference.');

    const cacheKey = `${initialReference}|${slug(context.colorName)}`;
    if (cache.has(cacheKey)) return cache.get(cacheKey);

    const promise = (async () => {
      try {
        const product = await fetchJson(productJsonUrl(initialReference));
        return { product, productUrl: normalizeProductUrl(product.url || initialReference) };
      } catch (error) {
        if (error.status !== 404) throw error;
        const exactUrl = await searchExactProduct(context.colorName, context.productNumber, context.baseTitle);
        if (!exactUrl) throw error;
        const product = await fetchJson(productJsonUrl(exactUrl));
        return { product, productUrl: exactUrl };
      }
    })();

    cache.set(cacheKey, promise);
    try {
      return await promise;
    } catch (error) {
      cache.delete(cacheKey);
      throw error;
    }
  }

  function readInitialProduct() {
    const node = document.querySelector('[data-me-initial-product-json]');
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch (_) { return null; }
  }

  function optionNames(product) {
    return (product?.options || []).map((option) => typeof option === 'string' ? option : option?.name || '');
  }

  function optionIndex(product, accepted) {
    const names = optionNames(product).map((name) => text(name).toLowerCase());
    return names.findIndex((name) => accepted.some((token) => name === token || name.includes(token)));
  }

  function variants(product) {
    return Array.isArray(product?.variants) ? product.variants : [];
  }

  function variantOption(variant, index) {
    return variant?.options?.[index] ?? variant?.[`option${index + 1}`] ?? '';
  }

  function matchingVariant(product, colorName, sizeName = '') {
    const colorIndex = optionIndex(product, ['color', 'colour']);
    const sizeIndex = optionIndex(product, ['size']);
    const targetColor = slug(colorName);
    const targetSize = text(sizeName).toLowerCase();
    return variants(product).find((variant) => {
      if (variant.available === false) return false;
      const colorMatches = colorIndex < 0 || !targetColor || slug(variantOption(variant, colorIndex)) === targetColor;
      const sizeMatches = sizeIndex < 0 || !targetSize || text(variantOption(variant, sizeIndex)).toLowerCase() === targetSize;
      return colorMatches && sizeMatches;
    }) || variants(product).find((variant) => variant.available !== false) || variants(product)[0] || null;
  }

  function isMultiSizeMode() {
    return Boolean(document.querySelector('[data-me-order-mode="multi"].is-active, [data-me-order-mode="multi"][aria-pressed="true"]'));
  }

  function selectedSize() {
    // Multi-size projects do not have one selected Shopify size variant.
    // Returning the hidden "Multiple Sizes" property here previously caused a
    // color click to masquerade as a valid size selection.
    if (isMultiSizeMode()) return '';
    const active = document.querySelector('[data-me-size-picker] [data-me-size-button].is-active');
    const property = document.querySelector('[data-me-size-picker] [data-me-size-property]');
    return text(active?.dataset.sizeValue || property?.value);
  }

  function productImages(product, variant) {
    const images = (product?.images || []).map(mediaUrl).filter(Boolean);
    const front = mediaUrl(variant?.featured_image || variant?.featured_media || variant?.image) || mediaUrl(product?.featured_image) || images[0] || '';
    return {
      frontImage: front,
      backImage: images.find((url) => /back/i.test(url)) || images[1] || front,
      leftSleeveImage: images.find((url) => /left[-_ ]?sleeve/i.test(url)) || images[2] || front,
      rightSleeveImage: images.find((url) => /right[-_ ]?sleeve/i.test(url)) || images[3] || front,
      images
    };
  }

  function updateSwatches(handle, colorName, productUrl) {
    colorSections().forEach((section) => {
      section.dataset.meCurrentHandle = handle;
      section.dataset.meCurrentColor = colorName;
      section.dataset.meCurrentProductUrl = productUrl || '';
      const label = section.querySelector('[data-me-selected-color]');
      if (label) label.textContent = `Selected: ${colorName}`;
      const stock = section.querySelector('[data-me-selected-color-stock]');
      if (stock) {
        stock.textContent = '● In Stock';
        stock.classList.add('is-stock');
        stock.classList.remove('is-backorder');
      }
      const message = section.querySelector('[data-me-color-message]');
      if (message) message.textContent = '';
      section.querySelectorAll('[data-me-designer-color]').forEach((button) => {
        const active = button.dataset.colorHandle === handle || slug(button.dataset.colorName) === slug(colorName);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
        if (active && productUrl) {
          button.dataset.productUrl = productUrl;
          button.dataset.colorHandle = handle;
        }
      });
    });
  }

  function updateTitles(product, colorName) {
    const title = text(product?.title) || colorName;
    document.querySelectorAll('.me-pp-summary h1, .me-main-product__panel h1').forEach((node) => { node.textContent = title; });
    document.querySelectorAll('[data-me-designer] .me-designer__brand h2').forEach((node) => { node.textContent = `Personalize ${title}`; });
    document.querySelectorAll('[data-me-designer]').forEach((node) => { node.dataset.meProductTitle = title; });
  }

  function updateGallery(frontImage, colorName, images = []) {
    if (!frontImage) return;
    document.querySelectorAll('.me-pp-gallery__main-image, .me-main-product__image-main img').forEach((image) => {
      image.src = frontImage;
      image.srcset = '';
      image.alt = colorName ? `${colorName} garment` : image.alt;
    });
    if (images.length) {
      document.querySelectorAll('[data-me-pp-thumbs]').forEach(container => {
        container.innerHTML = images.map((src,index) => `<button type="button" class="me-pp-gallery__thumb${index===0?' is-active':''}" data-me-pp-thumb data-index="${index}" data-src="${src}" data-full-src="${src}" data-alt="${colorName || 'Garment'} image ${index+1}" aria-label="View ${colorName || 'garment'} image ${index+1}" aria-pressed="${index===0?'true':'false'}"><img src="${src}" alt="${colorName || 'Garment'} image ${index+1}" loading="lazy"></button>`).join('');
      });
      document.querySelectorAll('.me-main-product__thumbs').forEach(container => {
        container.innerHTML = images.map((src,index) => `<button type="button" class="me-main-product__thumb" data-me-product-thumb="${src}" aria-label="View ${colorName || 'garment'} image ${index+1}"><img src="${src}" alt="${colorName || 'Garment'} image ${index+1}" loading="lazy"></button>`).join('');
      });
    }
  }

  function updateSizePickers(product) {
    const sizeIndex = optionIndex(product, ['size']);
    const values = [];
    if (sizeIndex >= 0) {
      variants(product).forEach((variant) => {
        if (variant.available === false) return;
        const value = text(variantOption(variant, sizeIndex));
        if (value && !values.includes(value)) values.push(value);
      });
    }
    document.querySelectorAll('[data-me-size-picker]').forEach((picker) => {
      const grid = picker.querySelector('[data-me-size-grid]');
      if (!grid || !values.length) return;
      const previous = text(picker.querySelector('[data-me-size-property]')?.value);
      grid.innerHTML = values.map((value) => `<button type="button" class="me-size-picker__button${value === previous ? ' is-active' : ''}" data-me-size-button data-size-value="${value.replace(/"/g, '&quot;')}" aria-pressed="${value === previous ? 'true' : 'false'}"><span>${value}</span></button>`).join('');
      grid.hidden = false;
      if (previous && !values.includes(previous)) {
        const property = picker.querySelector('[data-me-size-property]');
        const label = picker.querySelector('[data-me-selected-size]');
        const message = picker.querySelector('[data-me-size-message]');
        if (property) property.value = '';
        if (label) label.textContent = 'Size selection required';
        if (message) {
          if (isMultiSizeMode()) {
            message.textContent = 'Size quantities are managed in the multi-size distribution.';
            message.classList.remove('is-required');
            message.classList.add('is-success');
          } else {
            message.textContent = 'Select a garment size to continue.';
            message.classList.add('is-required');
            message.classList.remove('is-success');
          }
        }
      }
    });
  }

  function normalizeSizeToken(value) {
    return text(value)
      .toUpperCase()
      .replace(/[._-]+/g, ' ')
      .replace(/\b(YOUTH|ADULT|WOMEN|WOMENS|MEN|MENS|UNISEX|SIZE|SIZES)\b/g, ' ')
      .replace(/\s+/g, '')
      .replace(/^EXTRASMALL$/, 'XS')
      .replace(/^SMALL$/, 'S')
      .replace(/^MEDIUM$/, 'M')
      .replace(/^LARGE$/, 'L')
      .replace(/^EXTRALARGE$/, 'XL')
      .replace(/^([2-9])X$/, '$1XL')
      .replace(/^([2-9])XLARGE$/, '$1XL');
  }

  function buildDistributionVariantMap(product) {
    const map = {};
    const sizeIndex = optionIndex(product, ['size']);
    variants(product).forEach((variant) => {
      const candidates = [];
      if (sizeIndex >= 0) candidates.push(variantOption(variant, sizeIndex));
      if (Array.isArray(variant?.options)) candidates.push(...variant.options);
      candidates.push(variant?.option1, variant?.option2, variant?.option3);
      if (variant?.title && variant.title !== 'Default Title') candidates.push(...String(variant.title).split(/\s*\/\s*|,|\|/));
      const size = candidates.map(normalizeSizeToken).find((value) => /^(XS|S|M|L|XL|[2-9]XL)$/.test(value));
      if (size && variant?.id) map[size] = Number(variant.id);
    });
    window.MomentsDistributionVariantMap = map;
    return map;
  }

  function syncDistributionVariantIds(product) {
    const map = buildDistributionVariantMap(product);
    document.querySelectorAll('[data-me-size-quantity]').forEach((input) => {
      const key = normalizeSizeToken(input.dataset.meSizeValue || '');
      const id = Number(map[key] || 0);
      if (id) input.dataset.meVariantId = String(id);
      else delete input.dataset.meVariantId;
    });
    if (window.MomentsProjectState?.product) {
      window.MomentsProjectState.product.selectedColorProduct = product;
      window.MomentsProjectState.product.distributionVariantIds = { ...map };
    }
    return map;
  }

  function updateVariant(product, colorName) {
    const size = selectedSize();
    const variant = matchingVariant(product, colorName, size);
    buildDistributionVariantMap(product);
    if (!variant) return null;
    productForms().forEach((form) => {
      const input = form.querySelector('[data-me-variant-id]');
      if (input) input.value = String(variant.id);
    });
    window.dispatchEvent(new CustomEvent('me:designer-variant-changed', {
      detail: {
        variantId: variant.id,
        priceCents: variant.price,
        available: variant.available !== false,
        color: colorName,
        size,
        sizeSelected: !isMultiSizeMode() && Boolean(size)
      }
    }));
    return variant;
  }

  async function selectColor(button) {
    const colorName = text(button.dataset.colorName || button.getAttribute('aria-label'));
    const section = button.closest('[data-me-designer-colors]');
    const productNumber = text(section?.dataset.meProductNumber);
    const initialProduct = window.MomentsCurrentProduct || readInitialProduct();
    const baseTitle = text(initialProduct?.title).split(' - ')[0].trim();
    const reference = text(button.dataset.productUrl || button.dataset.colorHandle);
    if (!reference || !colorName) return;
    const serial = ++requestSerial;

    colorSections().forEach((node) => node.classList.add('is-loading'));
    try {
      const result = await fetchProduct(reference, { colorName, productNumber, baseTitle });
      if (serial !== requestSerial) return;
      const product = result.product;
      const productUrl = result.productUrl;
      const handle = text(product.handle) || productUrl.split('/').pop();

      window.MomentsCurrentProduct = product;
      window.MomentsSelectedColor = { handle, colorName, product, productUrl };

      updateSwatches(handle, colorName, productUrl);
      updateTitles(product, colorName);
      updateSizePickers(product);
      syncDistributionVariantIds(product);
      const variant = updateVariant(product, colorName);
      const media = productImages(product, variant);
      updateGallery(media.frontImage, colorName, media.images || []);

      window.dispatchEvent(new CustomEvent('me:product-media-changed', {
        detail: { ...media, colorName, variantId: variant?.id || null, productUrl, handle }
      }));
      window.dispatchEvent(new CustomEvent('me:designer-color-changed', {
        detail: { handle, colorName, product, productUrl, variantId: variant?.id || null, source: 'shared-controller' }
      }));
      window.dispatchEvent(new CustomEvent('me:variant-selection-state', {
        detail: { colorSelected: true, color: colorName, sizeSelected: !isMultiSizeMode() && Boolean(selectedSize()), size: isMultiSizeMode() ? '' : selectedSize(), variantId: variant?.id || null, distributionVariantIds: window.MomentsDistributionVariantMap || {} }
      }));
    } catch (error) {
      console.error('[Moments shared color controller]', error);
      document.querySelectorAll('[data-me-color-message]').forEach((node) => {
        node.textContent = `${colorName} product data could not be loaded. Please choose another color or refresh the page.`;
      });
    } finally {
      colorSections().forEach((node) => node.classList.remove('is-loading'));
    }
  }

  function initializeCurrentProduct() {
    const product = readInitialProduct();
    if (!product) return;
    window.MomentsCurrentProduct = product;
    const currentSection = colorSections()[0];
    const colorName = text(currentSection?.dataset.meCurrentColor) || text(product.title).split(' - ').pop();
    const productUrl = normalizeProductUrl(product.url || `/products/${product.handle}`);
    window.MomentsSelectedColor = { handle: product.handle, colorName, product, productUrl };
    updateSwatches(product.handle, colorName, productUrl);
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-me-designer-color]');
    if (!button) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    selectColor(button);
  }, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeCurrentProduct, { once: true });
  } else {
    initializeCurrentProduct();
  }

  window.MomentsColorController = { selectColor, fetchProduct, syncDistributionVariantIds };
})();
